// Today — the dashboard a bookkeeper opens first.
//
// Greeting and how much is waiting; four figures; what waits for you beside
// what just happened; and this month's spend, approvals and VAT return.
//
// Every number is read, never computed from a page of rows:
//
//   * waiting — GET /work's counts, the same read the sidebar badge shows (it
//     is passed in from App, so the two cannot disagree);
//   * booked this month — /insights/pipeline `confirmedThisMonth`, and the
//     card opens Documents on /insights/booked, the same query as a list;
//   * time saved — /insights/time-saved, an estimate that prints its basis;
//   * VAT — /insights/vat-period.
//
// Each figure fails on its own. A card whose read failed says so in place, and
// a read that succeeded with nothing to show says THAT; the two never look alike.

import { useState, type ReactNode } from 'react';
import type {
  AiActivity,
  AiActivityEvent,
  ApprovalSplit,
  PipelineCounts,
  SpendByLedger,
  TimeSavedEstimate,
  VatPeriodSummary,
  WorkItem,
  WorkQueue,
} from '../api/types.ts';
import { CHART_COLOURS, Ring, sliceColour } from '../components/Charts.tsx';
import { PROVIDER_NAMES } from '../components/ActorAvatar.tsx';
import { Dialog } from '../components/Dialog.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import type { ScreenId } from '../components/Shell.tsx';
import { UploadDocument } from '../components/UploadDocument.tsx';
import { documentOf } from '../components/WorkItemLine.tsx';
import * as format from '../format.ts';
import { en, type MessageKey } from '../i18n/en.ts';
import { longDay, relativeTime, shortDate } from '../i18n/format.ts';
import { useLocale, useT, type Translate } from '../i18n/index.tsx';
import { sum } from '../money.ts';
import { openWorkTotal } from '../state/openWork.ts';
import { filterQuery, useRead, type Read } from '../state/useRead.ts';
import { administrationFilter, useWorkspace } from '../state/workspace.tsx';

/** How often "Just happened" is read again: often enough to watch a document go through. */
export const FEED_POLL_MS = 5000;
/** Rows in "Wacht op jouw akkoord"; the rest is one click away. */
export const LIST_ROWS = 6;
/** Rows in "Net gebeurd": the design shows four beside six decisions. */
export const FEED_ROWS = 4;
/** Ledgers drawn before the rest fold into "Other". */
export const SPEND_SEGMENTS = 4;

export interface TodayScreenProps {
  /** GET /work for the selection, read once in App for this screen and the badge. */
  readonly work: Read<WorkQueue>;
  readonly onOpenDocument: (documentId: string) => void;
  /** Documents, filtered on what was booked this month. */
  readonly onOpenBooked: () => void;
  readonly onNavigate: (screen: ScreenId) => void;
}

type Open = 'saved' | 'vat' | 'upload' | null;

export function TodayScreen({ work, onOpenDocument, onOpenBooked, onNavigate }: TodayScreenProps): ReactNode {
  const workspace = useWorkspace();
  const t = useT();
  const { locale } = useLocale();
  const [open, setOpen] = useState<Open>(null);
  const filter = filterQuery(administrationFilter(workspace.administrationId));

  const pipeline = useRead<PipelineCounts>(`/insights/pipeline${filter}`, 'the pipeline');
  const saved = useRead<TimeSavedEstimate>(`/insights/time-saved${filter}`, 'the time saved');
  const vat = useRead<VatPeriodSummary>(`/insights/vat-period${filter}`, 'the VAT period');
  const spend = useRead<SpendByLedger>(`/insights/spend${filter}`, 'the spend');
  const approvals = useRead<ApprovalSplit>(`/insights/approvals${filter}`, 'the approvals');
  const feed = useRead<AiActivity>(`/ai-activity${filter}`, 'the activity', { pollMs: FEED_POLL_MS });

  const waiting = openWorkTotal(work.data);
  const upload = () => setOpen('upload');
  const month = capitalise(new Intl.DateTimeFormat(locale === 'nl' ? 'nl-NL' : 'en-GB', { month: 'long', timeZone: 'Europe/Amsterdam' }).format(new Date()));
  const packageName = singlePackage(workspace.administrations.map((a) => a.connection?.provider ?? null)) ?? t('feed.package');

  return (
    <div className="today" data-testid="today">
      <header className="today-head">
        <div className="today-title">
          <h1 data-testid="today-greeting">{greeting(new Date(), workspace.me.user.displayName ?? null, t)}</h1>
          <p className="today-line" data-testid="today-waiting-line">
            {longDay(new Date(), locale)}. {waiting === null ? '' : t('today.waitingLine', { count: waiting })}
          </p>
        </div>
        <button type="button" className="btn primary big" onClick={upload} data-testid="today-upload">
          <Icon name="upload" size={16} />
          {t('today.upload')}
        </button>
      </header>

      <div className="kpis">
        <Kpi
          testId="kpi-waiting"
          icon="clock"
          tone="amber"
          tag={t('today.kpi.waitingTag')}
          label={t('today.kpi.waiting')}
          value={waiting === null ? null : format.count(waiting)}
          hint={t('today.kpi.waitingHint')}
          failed={work.error !== null && work.data === null}
          onClick={() => onNavigate('work')}
        />
        <Kpi
          testId="kpi-booked"
          icon="check"
          tone="green"
          tag={month}
          label={t('feed.operation.confirmed', { package: packageName })}
          value={pipeline.data === null ? null : format.count(pipeline.data.confirmedThisMonth)}
          hint={t('today.kpi.bookedHint')}
          failed={pipeline.error !== null && pipeline.data === null}
          onClick={onOpenBooked}
        />
        <Kpi
          testId="kpi-saved"
          icon="bolt"
          tone="blue"
          tag={month}
          label={t('today.kpi.saved')}
          value={saved.data === null ? null : t('today.duration', hoursAndMinutes(saved.data.savedMinutes))}
          hint={t('today.kpi.savedHint')}
          failed={saved.error !== null && saved.data === null}
          onClick={() => setOpen('saved')}
        />
        <Kpi
          testId="kpi-vat"
          icon="calendar"
          tone="violet"
          tag={vat.data?.label ?? ''}
          label={t('today.kpi.vat')}
          value={
            vat.data === null
              ? null
              : `${format.count(vat.data.daysLeft)} ${t('common.days', { count: vat.data.daysLeft })}`
          }
          hint={vat.data === null ? '' : t('today.kpi.vatHint', { label: vat.data.label, due: shortDate(vat.data.dueDate, locale) })}
          failed={vat.error !== null && vat.data === null}
          onClick={() => setOpen('vat')}
        />
      </div>

      <div className="today-lists">
        <WaitingList work={work} onOpenDocument={onOpenDocument} onNavigate={onNavigate} onUpload={upload} />
        <RecentList feed={feed} onOpenDocument={onOpenDocument} onNavigate={onNavigate} onUpload={upload} />
      </div>

      <div className="today-figures">
        <SpendCard read={spend} month={month.toLowerCase()} onNavigate={onNavigate} />
        <ApprovedCard read={approvals} onNavigate={onNavigate} />
        <VatCard read={vat} onDetails={() => setOpen('vat')} />
      </div>

      {open === 'saved' ? (
        <Dialog title={t('today.saved.dialogTitle')} onClose={() => setOpen(null)} testId="saved-dialog">
          <SavedBreakdown read={saved} />
        </Dialog>
      ) : open === 'vat' ? (
        <Dialog title={t('today.vat.details')} onClose={() => setOpen(null)} testId="vat-dialog">
          <VatDetails read={vat} />
        </Dialog>
      ) : open === 'upload' ? (
        <Dialog title={t('upload.title')} onClose={() => setOpen(null)} testId="upload-dialog">
          <UploadDocument
            tenantId={workspace.membership.tenantId}
            token={workspace.token}
            onUploaded={(documentId) => {
              setOpen(null);
              void work.reload();
              onOpenDocument(documentId);
            }}
          />
        </Dialog>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** "Goedemorgen, Samim" — by the office's clock; no name when none is set. */
export function greeting(now: Date, displayName: string | null, t: Translate): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Amsterdam', hour: '2-digit', hourCycle: 'h23' }).format(now),
  );
  const part = t(hour < 12 ? 'today.greeting.morning' : hour < 18 ? 'today.greeting.afternoon' : 'today.greeting.evening');
  const first = displayName?.trim().split(/\s+/)[0];
  return first === undefined || first === '' ? part : `${part}, ${first}`;
}

type Tone = 'amber' | 'green' | 'blue' | 'violet' | 'navy' | 'neutral';

function Kpi({
  testId,
  icon,
  tone,
  tag,
  label,
  value,
  hint,
  failed,
  onClick,
}: {
  readonly testId: string;
  readonly icon: IconName;
  readonly tone: Tone;
  /** Top right: what the figure is about — "Nu doen", the month, the VAT period. */
  readonly tag: string;
  readonly label: string;
  readonly value: string | null;
  /** Where the card leads; its tooltip and its accessible description. */
  readonly hint: string;
  readonly failed: boolean;
  readonly onClick: () => void;
}): ReactNode {
  const t = useT();
  return (
    <button type="button" className="kpi" data-testid={testId} onClick={onClick} title={hint}>
      <span className="kpi-top">
        <span className={`tile big ${tone}`} aria-hidden="true">
          <Icon name={icon} size={22} />
        </span>
        <span className={`kpi-tag ${tone}`}>{tag}</span>
      </span>
      <span className="kpi-figure">
        <span className="kpi-value" data-testid={`${testId}-value`}>
          {failed ? <small>{t('today.kpi.unavailable')}</small> : (value ?? '…')}
        </span>
        <span className="kpi-label">{label}</span>
      </span>
    </button>
  );
}

/** "1 u 24 m": whole hours and the minutes left over. */
export function hoursAndMinutes(minutes: number): { hours: number; minutes: number } {
  const whole = Math.max(0, Math.round(minutes));
  return { hours: Math.floor(whole / 60), minutes: whole % 60 };
}

/** The one accounting package every administration in view uses, or null when they differ or none is connected. */
function singlePackage(providers: readonly (string | null)[]): string | null {
  const distinct = [...new Set(providers)];
  if (distinct.length !== 1 || distinct[0] === null || distinct[0] === undefined) return null;
  return PROVIDER_NAMES[distinct[0]] ?? distinct[0];
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function ReadFailed({ message }: { readonly message: string }): ReactNode {
  return (
    <p className="dash-failed" role="status">
      {message}
    </p>
  );
}

// --- Wacht op jouw akkoord ----------------------------------------------------

/**
 * One row per decision. A document and its own proposal are the same decision —
 * approving the proposal is how the document is finished — so where both are on
 * the page the document row is folded into the proposal's.
 */
export function decisionRows(items: readonly WorkItem[]): readonly WorkItem[] {
  const proposed = new Set(
    items
      .filter((item) => item.kind === 'proposal' && item.summary?.documentId != null)
      .map((item) => item.summary?.documentId),
  );
  return items.filter((item) => !(item.kind === 'document' && proposed.has(item.id)));
}

function kindLabel(item: WorkItem, t: Translate): string {
  if (item.kind === 'document') {
    const type = item.summary?.documentType ?? item.detail ?? '';
    const key = `work.docType.${type}`;
    return key in en ? t(key as MessageKey) : t('work.kind.document');
  }
  return t(`work.kind.${item.kind}`);
}

/** A document the reading stopped on: shown as "Controleren" rather than as ready to approve. */
function needsCheck(item: WorkItem): boolean {
  return item.kind === 'document' && (item.state === 'needs_review' || item.state === 'quarantined');
}

function WaitingList({
  work,
  onOpenDocument,
  onNavigate,
  onUpload,
}: {
  readonly work: Read<WorkQueue>;
  readonly onOpenDocument: (documentId: string) => void;
  readonly onNavigate: (screen: ScreenId) => void;
  readonly onUpload: () => void;
}): ReactNode {
  const t = useT();
  const { locale } = useLocale();
  const rows = decisionRows(work.data?.items ?? []).slice(0, LIST_ROWS);
  return (
    <section className="dash-card list-card waiting-card" data-testid="waiting-list" aria-labelledby="waiting-title">
      <div className="dash-head">
        <h2 id="waiting-title">{t('today.waiting.title')}</h2>
        <button type="button" className="btn text" onClick={() => onNavigate('work')} data-testid="waiting-all">
          {t('common.viewAll')}
        </button>
      </div>
      {work.data === null ? (
        work.error === null ? (
          <p className="dash-loading">{t('common.reading')}</p>
        ) : (
          <ReadFailed message={work.error} />
        )
      ) : rows.length === 0 ? (
        <EmptyState
          testId="waiting-empty"
          icon="done"
          text={t('today.waiting.empty')}
          action={{ label: t('today.upload'), onClick: onUpload }}
        />
      ) : (
        <ul className="dash-rows">
          {rows.map((item) => {
            const documentId = documentOf(item);
            const open = () => {
              if (documentId !== null) onOpenDocument(documentId);
              else if (item.kind === 'bank_match') onNavigate('bank');
              else if (item.kind === 'request') onNavigate('messages');
              else onNavigate('work');
            };
            const invoice = item.summary?.invoiceNumber ?? null;
            const check = needsCheck(item);
            const line =
              item.kind === 'bank_match' && invoice !== null
                ? t('today.waiting.paymentFound', { invoice })
                : [[kindLabel(item, t), invoice].filter((part) => part !== null).join(' '), shortDate(item.orderedAt.slice(0, 10), locale)].join(', ');
            const tone: Tone = check ? 'amber' : item.kind === 'bank_match' ? 'blue' : item.kind === 'request' ? 'violet' : 'green';
            const icon: IconName = check ? 'alert' : item.kind === 'bank_match' ? 'swap' : item.kind === 'request' ? 'mail' : 'file';
            return (
              <li key={`${item.kind}:${item.id}`} className="dash-row" data-testid={`waiting-${item.kind}-${item.id}`}>
                <span className={`tile ${tone}`} aria-hidden="true">
                  <Icon name={icon} size={20} />
                </span>
                <div className="grow">
                  <strong>{item.summary?.supplierName ?? kindLabel(item, t)}</strong>
                  <p>{line}</p>
                </div>
                {check ? (
                  <span className="pill amber">{t('today.waiting.check')}</span>
                ) : item.summary?.amount == null ? null : (
                  <span className="amt">{format.money(item.summary.amount)}</span>
                )}
                <button type="button" className={`btn row-btn ${check ? '' : 'primary'}`} onClick={open} data-testid="waiting-view">
                  {t('common.view')}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// --- Net gebeurd ----------------------------------------------------------------

/** What happened, in a bookkeeper's words: "Geboekt in e-Boekhouden". */
export function plainWords(event: AiActivityEvent, packageName: string, t: Translate): string {
  const key = `feed.${event.action}`;
  return key in en ? t(key as MessageKey, { package: packageName }) : t('feed.other');
}

function RecentList({
  feed,
  onOpenDocument,
  onNavigate,
  onUpload,
}: {
  readonly feed: Read<AiActivity>;
  readonly onOpenDocument: (documentId: string) => void;
  readonly onNavigate: (screen: ScreenId) => void;
  readonly onUpload: () => void;
}): ReactNode {
  const workspace = useWorkspace();
  const t = useT();
  const { locale } = useLocale();
  const providers = new Map(
    workspace.administrations.map((administration) => [administration.id, administration.connection?.provider ?? null]),
  );
  const events = (feed.data?.events ?? []).slice(0, FEED_ROWS);
  return (
    <section className="dash-card list-card recent-card" data-testid="recent-list" aria-labelledby="recent-title">
      <div className="dash-head">
        <h2 id="recent-title">{t('today.recent.title')}</h2>
        {feed.error === null ? (
          <span className="live-badge">
            <span className="live-dot" aria-hidden="true" />
            {t('today.recent.live')}
          </span>
        ) : null}
      </div>
      {feed.data === null ? (
        feed.error === null ? (
          <p className="dash-loading">{t('common.reading')}</p>
        ) : (
          <ReadFailed message={feed.error} />
        )
      ) : events.length === 0 ? (
        <EmptyState
          testId="recent-empty"
          icon="activity"
          text={t('today.recent.empty')}
          action={{ label: t('today.upload'), onClick: onUpload }}
        />
      ) : (
        <ul className="dash-rows" aria-live="polite">
          {events.map((event) => {
            const provider = providers.get(event.administrationId) ?? null;
            const packageName = provider === null ? t('feed.package') : (PROVIDER_NAMES[provider] ?? provider);
            const subject = [event.subject?.supplierName, event.subject?.invoiceNumber].filter(
              (part): part is string => part != null && part !== '',
            );
            const documentId = event.targetKind === 'document' ? event.targetId : null;
            const tone: Tone = event.action.includes('confirmed') || event.action.endsWith('approved')
              ? 'green'
              : event.action.includes('blocked') || event.action.includes('failed') || event.action.includes('needs_review')
                ? 'amber'
                : 'navy';
            const words = plainWords(event, packageName, t);
            return (
              <li key={event.id} className="dash-row" data-testid={`recent-${event.id}`}>
                <span className={`tile round ${tone}`} aria-hidden="true">
                  <Icon name={tone === 'green' ? 'check' : tone === 'amber' ? 'alert' : 'sparkle'} size={16} />
                </span>
                <div className="grow">
                  {documentId === null ? (
                    <strong>{words}</strong>
                  ) : (
                    <button type="button" className="link-plain" onClick={() => onOpenDocument(documentId)}>
                      <strong>{words}</strong>
                    </button>
                  )}
                  {subject.length === 0 ? null : <p>{subject.join(', ')}</p>}
                  <small>{relativeTime(event.occurredAt, locale, t)}</small>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="list-foot">
        <button type="button" className="btn text" onClick={() => onNavigate('activity')} data-testid="recent-all">
          {t('today.recent.all')}
        </button>
      </div>
    </section>
  );
}

// --- the bottom row -----------------------------------------------------------------

function SpendCard({
  read,
  month,
  onNavigate,
}: {
  readonly read: Read<SpendByLedger>;
  /** "november": the title says which month. */
  readonly month: string;
  readonly onNavigate: (screen: ScreenId) => void;
}): ReactNode {
  const t = useT();
  const categories = read.data?.categories ?? [];
  const currencies = [...new Set(categories.map((category) => category.currency))];
  const label = (category: SpendByLedger['categories'][number]): string => {
    if (category.ledgerName != null && category.ledgerName !== '') return category.ledgerName;
    if (category.ledgerCode != null) return t('today.spend.ledger', { code: category.ledgerCode });
    return t('today.spend.notSynced', { ref: category.ledgerAccountRef });
  };
  // One segment per ledger in the four chart colours; a fifth ledger and beyond
  // fold into "Other" with its exact total — within one currency only, where a
  // sum means something.
  const rows: { key: string; label: string; net: string; currency: string }[] =
    currencies.length === 1 && categories.length > SPEND_SEGMENTS
      ? [
          ...categories.slice(0, SPEND_SEGMENTS - 1).map((category) => ({
            key: `${category.ledgerAccountRef}:${category.currency}`,
            label: label(category),
            net: category.net,
            currency: category.currency,
          })),
          {
            key: 'other',
            label: t('today.spend.other', { count: categories.length - (SPEND_SEGMENTS - 1) }),
            net: sum(categories.slice(SPEND_SEGMENTS - 1).map((category) => category.net)),
            currency: currencies[0] as string,
          },
        ]
      : categories.map((category) => ({
          key: `${category.ledgerAccountRef}:${category.currency}`,
          label: label(category),
          net: category.net,
          currency: category.currency,
        }));
  const total =
    currencies.length === 1
      ? format.money({ amount: sum(categories.map((category) => category.net)), currency: currencies[0] as string })
      : t('today.spend.currencies', { count: currencies.length });
  const weights = rows.map((row) => Math.max(0, Number(row.net)));
  return (
    <section className="dash-card figure-card" data-testid="spend-card" aria-labelledby="spend-title">
      <div className="dash-head baseline">
        <h2 id="spend-title">{t('today.spend.title', { month })}</h2>
        <span className="dash-sub" title={t('today.spend.sub')}>{t('today.spend.exVat')}</span>
      </div>
      {read.data === null ? (
        read.error === null ? <p className="dash-loading">{t('common.reading')}</p> : <ReadFailed message={read.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          testId="spend-empty"
          icon="wallet"
          text={t('today.spend.empty')}
          action={{ label: t('today.toWork'), onClick: () => onNavigate('work') }}
        />
      ) : (
        <>
          <span className="figure-big" data-testid="spend-total">{total}</span>
          <div className="stack-bar" aria-hidden="true">
            {rows.map((row, i) => (
              <span key={row.key} style={{ flex: weights[i] ?? 0, background: row.key === 'other' ? CHART_COLOURS.grey : sliceColour(i) }} />
            ))}
          </div>
          <ul className="slices">
            {rows.map((row, i) => (
              <li key={row.key}>
                <span>
                  <span className="sq" style={{ background: row.key === 'other' ? CHART_COLOURS.grey : sliceColour(i) }} />
                  {row.label}
                </span>
                <span>{format.money({ amount: row.net, currency: row.currency })}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function ApprovedCard({
  read,
  onNavigate,
}: {
  readonly read: Read<ApprovalSplit>;
  readonly onNavigate: (screen: ScreenId) => void;
}): ReactNode {
  const t = useT();
  const split = read.data;
  const total = split === null ? 0 : split.unchanged + split.changedFirst;
  const share = total === 0 ? 0 : (split?.unchanged ?? 0) / total;
  return (
    <section className="dash-card figure-card" data-testid="approved-card" aria-labelledby="approved-title">
      <div className="dash-head">
        <h2 id="approved-title" title={t('today.approved.info')}>{t('today.approved.title')}</h2>
      </div>
      {split === null ? (
        read.error === null ? <p className="dash-loading">{t('common.reading')}</p> : <ReadFailed message={read.error} />
      ) : total === 0 ? (
        <EmptyState
          testId="approved-empty"
          icon="percent"
          text={t('today.approved.empty')}
          action={{ label: t('today.toWork'), onClick: () => onNavigate('work') }}
        />
      ) : (
        <div className="dash-figure">
          <Ring share={share} label={`${Math.round(share * 100)}%`} size={96} />
          <p className="figure-text" data-testid="approved-of">
            {t('today.approved.of', { unchanged: split.unchanged, total })}
          </p>
        </div>
      )}
    </section>
  );
}

function VatCard({ read, onDetails }: { readonly read: Read<VatPeriodSummary>; readonly onDetails: () => void }): ReactNode {
  const t = useT();
  const { locale } = useLocale();
  const vat = read.data;
  const counted = vat === null ? 0 : vat.documents.confirmed + vat.documents.open;
  const share = counted === 0 ? 0 : (vat?.documents.confirmed ?? 0) / counted;
  return (
    <section className="dash-card figure-card" data-testid="vat-card" aria-labelledby="vat-title">
      <div className="dash-head">
        <h2 id="vat-title">{vat === null ? t('today.vat.title') : `${t('today.vat.title')} ${vat.label}`}</h2>
        {vat === null ? null : (
          // The due date, as the design's pill; it opens the details.
          <button
            type="button"
            className="pill amber pill-button"
            onClick={onDetails}
            data-testid="vat-details"
            aria-label={`${t('today.vat.details')}, ${t('today.vat.due', { due: shortDate(vat.dueDate, locale) })}`}
          >
            {shortDate(vat.dueDate, locale)}
          </button>
        )}
      </div>
      {vat === null ? (
        read.error === null ? <p className="dash-loading">{t('common.reading')}</p> : <ReadFailed message={read.error} />
      ) : (
        <>
          <span className="figure-big">
            {format.count(vat.daysLeft)} {t('common.days', { count: vat.daysLeft })}
          </span>
          <div className="bar" aria-hidden="true">
            <span style={{ width: `${share * 100}%`, background: 'var(--violet)' }} />
          </div>
          <p className="figure-text">
            {counted === 0
              ? t('today.vat.noneDated', { label: vat.label })
              : t('today.vat.progress', { confirmed: vat.documents.confirmed, counted, open: vat.documents.open })}
          </p>
        </>
      )}
    </section>
  );
}

// --- the dialogs ----------------------------------------------------------------

function SavedBreakdown({ read }: { readonly read: Read<TimeSavedEstimate> }): ReactNode {
  const t = useT();
  const estimate = read.data;
  if (estimate === null) {
    return read.error === null ? <p>{t('common.reading')}</p> : <ReadFailed message={read.error} />;
  }
  const hours = (minutes: number) => t('today.hoursShort', { value: format.number(minutes / 60, 1) });
  return (
    <div data-testid="saved-breakdown">
      <p>{t('today.saved.formula')}</p>
      {estimate.documents === 0 ? (
        <p className="muted" style={{ marginTop: 12 }}>
          {t('today.saved.none')}
        </p>
      ) : null}
      <dl className="calc">
        <dt>{t('today.saved.documents')}</dt>
        <dd data-testid="saved-documents">{format.count(estimate.documents)}</dd>
        <dt>{t('today.saved.perDocument')}</dt>
        <dd>{t('today.minutes', { value: estimate.manualMinutesPerDocument })}</dd>
        <dt>{t('today.saved.manual')}</dt>
        <dd>{hours(estimate.manualMinutes)}</dd>
        <dt>{t('today.saved.measured', { measured: estimate.measuredDocuments, documents: estimate.documents })}</dt>
        <dd>− {hours(estimate.measuredReviewMinutes)}</dd>
        <dt className="total">{t('today.saved.result')}</dt>
        <dd className="total" data-testid="saved-result">
          {hours(estimate.savedMinutes)}
        </dd>
      </dl>
      <p className="muted small">{t('today.saved.cap', { cap: estimate.reviewMeasurementCapMinutes })}</p>
    </div>
  );
}

function VatDetails({ read }: { readonly read: Read<VatPeriodSummary> }): ReactNode {
  const t = useT();
  const { locale } = useLocale();
  const vat = read.data;
  if (vat === null) return read.error === null ? <p>{t('common.reading')}</p> : <ReadFailed message={read.error} />;
  const counted = vat.documents.confirmed + vat.documents.open;
  return (
    <div data-testid="vat-breakdown">
      <div className="dash-big">
        {format.count(vat.daysLeft)} <small>{t('today.vat.daysLeft', { count: vat.daysLeft })}</small>
      </div>
      <p className="muted">
        {t('today.vat.period', {
          label: vat.label,
          from: shortDate(vat.periodStart, locale),
          to: shortDate(vat.periodEnd, locale),
          due: shortDate(vat.dueDate, locale),
        })}
      </p>
      <dl className="calc">
        <dt>{t('today.vat.bookedLabel')}</dt>
        <dd data-testid="vat-confirmed">{format.count(vat.documents.confirmed)}</dd>
        <dt>{t('today.vat.openLabel')}</dt>
        <dd data-testid="vat-open">{format.count(vat.documents.open)}</dd>
      </dl>
      {counted === 0 ? <p className="muted small">{t('today.vat.noneDated', { label: vat.label })}</p> : null}
      {vat.documents.undated === 0 ? null : (
        <p className="muted small">{t('today.vat.undated', { count: vat.documents.undated })}</p>
      )}
    </div>
  );
}
